/**
 * Canonical App Store URL, in a plain module so BOTH server and client
 * components can import it. (Exporting it from AppStoreLink.tsx broke server
 * consumers: constants imported across a 'use client' boundary arrive as
 * opaque client references, so template literals rendered
 * "[object Object]?ppid=...".)
 */
export const APP_STORE_URL =
  'https://apps.apple.com/us/app/microplastics-food-scanner/id6741938548'

/**
 * Apple campaign attribution. App Analytics attributes a web-driven install
 * to a campaign only when the link carries the account's provider token (pt)
 * plus a campaign token (ct). Every CTA on the site now carries both, so
 * App Analytics -> Acquisition -> Campaigns breaks installs down by the
 * CTA's `source` label (home_hero, scanner_page_hero, blog_inline_*, ...).
 *
 * The pt is the account-wide provider token from the ASC campaign-link
 * generator (App Analytics -> Acquisition -> Campaigns). It is NOT a secret:
 * it appears in every public campaign link a user could click, so it lives
 * in code as the default. An env override stays available in case the
 * account's provider token ever changes.
 *
 * Campaign rows only appear once a link accrues installs above Apple's
 * privacy threshold (~5 unique accounts through that specific ct) with a
 * 24-48h reporting delay, which is why a freshly generated campaign shows
 * nothing until real click-through traffic flows to it.
 */
const ASC_PROVIDER_TOKEN =
  process.env.NEXT_PUBLIC_ASC_PROVIDER_TOKEN ?? '127578015'

/** Campaign tokens must be alphanumeric/dash/underscore for App Analytics. */
function sanitizeCampaignToken(source: string): string {
  return source.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40)
}

/**
 * Build the final store href for a CTA. Preserves custom product page /
 * in-app event hrefs (?ppid= / ?eventid=) and appends attribution params.
 */
export function appStoreHref(source: string, baseHref?: string): string {
  const base = baseHref ?? APP_STORE_URL
  const sep = base.includes('?') ? '&' : '?'
  const ct = sanitizeCampaignToken(source)
  const pt = ASC_PROVIDER_TOKEN ? `&pt=${ASC_PROVIDER_TOKEN}` : ''
  return `${base}${sep}ct=${ct}${pt}&mt=8`
}
